import {
  ConsentIdentityFormValues,
  ConsentIdentitySchema,
} from '@act/shared/data-transfer-objects';
import type { IdentitySubmissionRequest } from '@act/shared/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Control, FormState, useForm } from 'react-hook-form';
import { consentRequests } from 'redux/requests/consents.requests';
import { consentIdentitySuccess } from 'redux/slices/consent.slice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import './consents.scss';

interface UseConsentIdentityFormProps {
  linkKey?: string; // only used for member facing consents
}

export const useConsentIdentityForm = ({
  linkKey,
}: UseConsentIdentityFormProps): {
  formState: FormState<ConsentIdentityFormValues>;
  onSubmit: () => Promise<void>;
  control: Control<ConsentIdentityFormValues>;
} => {
  const dispatch = useAppDispatch();
  const [
    identityFormResponse,
    _isSubmittingIdentityForm,
    submitIdentityForm,
    identityFormError,
  ] = useApiCall(consentRequests.postIdentityForm);
  const [
    identityResponse,
    _isSubmittingIdentity,
    submitIdentity,
    identityError,
  ] = useApiCall(consentRequests.postIdentity);

  const defaultValues: Partial<ConsentIdentityFormValues> = {
    name: undefined,
    dateOfBirth: undefined,
    email: '',
    line1: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
  };

  const { control, handleSubmit, setError, reset, formState } =
    useForm<ConsentIdentityFormValues>({
      mode: 'onChange',
      defaultValues,
      resolver: zodResolver(ConsentIdentitySchema),
    });

  useEffect(() => {
    reset();
  }, [linkKey]);

  useEffect(() => {
    const errors = identityFormError?.errors || identityError?.errors;
    if (!errors) return;

    if (errors?.length) {
      for (const error of errors) {
        const message = Object.values(error.constraints).join(' ');
        const type = Object.keys(error.constraints)[0];
        setError(error.property as keyof ConsentIdentityFormValues, {
          type: type,
          message: message,
        });
      }
    }
  }, [identityError, identityFormError]);

  const handleSubmission = async (data: ConsentIdentityFormValues) => {
    const body = (
      linkKey ? { ...data, consentLinkKey: linkKey } : { ...data }
    ) as IdentitySubmissionRequest;
    if (linkKey) {
      submitIdentityForm({ params: null, body });
    } else {
      submitIdentity({ params: null, body });
    }
  };

  useEffect(() => {
    if (identityResponse) {
      dispatch(consentIdentitySuccess(identityResponse));
    }
  }, [identityResponse, dispatch]);

  useEffect(() => {
    if (identityFormResponse) {
      dispatch(consentIdentitySuccess(identityFormResponse));
    }
  }, [identityFormResponse, dispatch]);

  return {
    formState,
    onSubmit: handleSubmit(handleSubmission),
    control,
  };
};
